/**
 * useChatMetadata — Manages chat-level and message-level metadata state.
 *
 * Extracted from ChatContainer to reduce orchestrator size.
 * Owns: metadata (chat + message), metadataModal, messageMetadataList,
 *       expandedMetadata, and fileMetadataModal.
 */

import { useState, useCallback } from "react";

export const useChatMetadata = () => {
  const [metadata, setMetadata] = useState({ chat: {}, message: {} });
  const [metadataModal, setMetadataModal] = useState(false);
  const [messageMetadataList, setMessageMetadataList] = useState([]);
  const [expandedMetadata, setExpandedMetadata] = useState({});
  const [fileMetadataModal, setFileMetadataModal] = useState({
    open: false,
    fileName: "",
    metadata: null,
  });

  const openMetadataModal = useCallback(() => setMetadataModal(true), []);
  const closeMetadataModal = useCallback(() => setMetadataModal(false), []);

  const handleMetadataSave = useCallback((newMetadata) => {
    setMetadata(newMetadata);
    setMetadataModal(false);
  }, []);

  const toggleMetadata = useCallback((messageIndex) => {
    setExpandedMetadata((prev) => ({
      ...prev,
      [messageIndex]: !prev[messageIndex],
    }));
  }, []);

  const openFileMetadata = useCallback((fileName, meta) => {
    setFileMetadataModal({ open: true, fileName, metadata: meta });
  }, []);

  const closeFileMetadata = useCallback(() => {
    setFileMetadataModal({ open: false, fileName: "", metadata: null });
  }, []);

  const recordSentMetadata = useCallback(
    (messageIndex) => {
      const hasMessageMetadata =
        metadata.message && Object.keys(metadata.message).length > 0;
      if (hasMessageMetadata) {
        setMessageMetadataList((prev) => [
          ...prev,
          { messageIndex, metadata: metadata.message },
        ]);
        setMetadata((prev) => ({ ...prev, message: {} }));
      }
    },
    [metadata.message],
  );

  const resetMetadata = useCallback(() => {
    setMetadata({ chat: {}, message: {} });
    setMetadataModal(false);
    setMessageMetadataList([]);
    setExpandedMetadata({});
  }, []);

  return {
    metadata,
    metadataModal,
    messageMetadataList,
    expandedMetadata,
    fileMetadataModal,
    openMetadataModal,
    closeMetadataModal,
    handleMetadataSave,
    toggleMetadata,
    openFileMetadata,
    closeFileMetadata,
    recordSentMetadata,
    resetMetadata,
  };
};
